import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import InputField from '../../components/ui/InputField';
import PlayArrowIcon from '../../components/icons/PlayArrowIcon';
import { db } from '../../utils/firebase';
import { collection, query, where, getDocs, doc, updateDoc, onSnapshot, arrayUnion, arrayRemove } from 'firebase/firestore';
import { MafiaGameRoom, MafiaPlayer, MafiaRole } from '../../types/game';

const getRoleName = (role: MafiaRole) => {
  const roleNames: Record<MafiaRole, string> = {
    mafia: '마피아', citizen: '시민', police: '경찰', doctor: '의사', detective: '탐정'
  };
  return roleNames[role];
};

const getRoleDescription = (role: MafiaRole) => {
  const descriptions: Record<MafiaRole, string> = {
    mafia: '밤에 한 명을 선택하여 처형할 수 있습니다. 마피아끼리 서로를 알 수 있습니다.',
    citizen: '특별한 능력이 없는 일반 시민입니다. 투표로 마피아를 찾아내세요.',
    police: '밤에 한 명을 선택하여 마피아인지 확인할 수 있습니다.',
    doctor: '밤에 한 명을 선택하여 마피아의 공격으로부터 보호할 수 있습니다.',
    detective: '밤에 한 명을 선택하여 그 사람의 역할을 확인할 수 있습니다.'
  };
  return descriptions[role];
};

const MafiaGameJoin: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState('');
  const [gameCode, setGameCode] = useState(code || '');
  const [gameRoom, setGameRoom] = useState<MafiaGameRoom | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [showRole, setShowRole] = useState(false);

  const findGameRoom = async () => {
    if (!gameCode.trim()) {
      setError('초대 코드를 입력해주세요.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'mafiaGames'), where('code', '==', gameCode.toUpperCase()));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        setError('게임 방을 찾을 수 없습니다. 코드를 확인해주세요.');
        setLoading(false);
        return;
      }
      const roomDoc = querySnapshot.docs[0];
      const roomData = roomDoc.data() as any;
      if (roomData.status === 'finished') {
        setError('이미 종료된 게임입니다.');
        setLoading(false);
        return;
      }
      setGameRoom({
        id: roomDoc.id,
        ...roomData,
        createdAt: roomData.createdAt?.toDate ? roomData.createdAt.toDate() : (roomData.createdAt || new Date()),
        players: (roomData.players || []).map((p: any) => ({
          ...p,
          joinedAt: p.joinedAt?.toDate ? p.joinedAt.toDate() : (p.joinedAt || new Date())
        }))
      } as MafiaGameRoom);
    } catch (err: any) {
      setError('게임 방을 찾는 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const joinGame = async () => {
    if (!playerName.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (!gameRoom) {
      await findGameRoom();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const existingPlayer = gameRoom.players.find(p => p.name === playerName.trim());
      if (existingPlayer) {
        setPlayerId(existingPlayer.id);
        setLoading(false);
        return;
      }
      const newPlayer: MafiaPlayer = {
        id: `player_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        name: playerName.trim(),
        isAlive: true,
        joinedAt: new Date(),
        isReady: false
      };
      await updateDoc(doc(db, 'mafiaGames', gameRoom.id), { players: arrayUnion(newPlayer) });
      setPlayerId(newPlayer.id);
    } catch (err: any) {
      setError('게임 참가에 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const leaveGame = async () => {
    if (!gameRoom || !playerId) return;
    try {
      const playerToRemove = gameRoom.players.find(p => p.id === playerId);
      if (playerToRemove) {
        await updateDoc(doc(db, 'mafiaGames', gameRoom.id), { players: arrayRemove(playerToRemove) });
      }
      setPlayerId(null);
      setPlayerName('');
      setGameRoom(null);
      navigate('/tools/mafia-game');
    } catch (err: any) {
      setError('게임 나가기에 실패했습니다: ' + err.message);
    }
  };

  useEffect(() => { if (code) findGameRoom(); }, [code]);

  useEffect(() => {
    if (!gameRoom?.id) return;
    const unsub = onSnapshot(doc(db, 'mafiaGames', gameRoom.id), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as any;
        setGameRoom({
          id: snapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
          players: (data.players || []).map((p: any) => ({
            ...p,
            joinedAt: p.joinedAt?.toDate ? p.joinedAt.toDate() : (p.joinedAt || new Date())
          }))
        } as MafiaGameRoom);
      }
    });
    return () => unsub();
  }, [gameRoom?.id]);

  const currentPlayer = gameRoom?.players.find(p => p.id === playerId);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ marginBottom: 16, fontSize: 24, fontWeight: 700 }}>마피아 게임 참가</h1>
      {error && (
        <div style={{ marginBottom: 16, padding: 12, background: '#ffebee', borderRadius: 8, color: '#c62828' }}>
          {error}
          <button type="button" onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {!gameRoom ? (
        <Card padding="32px">
          <InputField label="초대 코드" value={gameCode} onChange={(v) => setGameCode(v.toUpperCase())} placeholder="게임 코드를 입력하세요" />
          <div style={{ marginTop: 24 }}>
            <Button variant="primary" onClick={findGameRoom} disabled={loading || !gameCode.trim()} leftIcon={<PlayArrowIcon width={20} height={20} />} style={{ width: '100%' }}>
              {loading ? '찾는 중...' : '게임 방 찾기'}
            </Button>
          </div>
        </Card>
      ) : !playerId ? (
        <Card padding="32px">
          <h2 style={{ marginBottom: 8, fontSize: 18 }}>{gameRoom.teacherName} 선생님의 게임 방</h2>
          <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>참가자: {gameRoom.players.length}명</p>
          <InputField label="이름" value={playerName} onChange={setPlayerName} placeholder="이름을 입력하세요" />
          <div style={{ marginTop: 24 }}>
            <Button variant="primary" onClick={joinGame} disabled={loading || !playerName.trim()} leftIcon={<PlayArrowIcon width={20} height={20} />} style={{ width: '100%' }}>
              {loading ? '참가 중...' : '게임 참가'}
            </Button>
          </div>
        </Card>
      ) : (
        <Card padding="32px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ margin: 0, fontSize: 20 }}>{gameRoom.teacherName} 선생님의 게임 방</h2>
            <Button variant="outline" onClick={leaveGame}>나가기</Button>
          </div>
          {currentPlayer?.role && gameRoom.status === 'role-assigned' && (
            <Card padding="16px" style={{ marginBottom: 24, background: showRole ? '#e3f2fd' : '#e0e0e0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>당신의 역할</h3>
                  {showRole ? (
                    <>
                      <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--Color-fill-brand-color)' }}>{getRoleName(currentPlayer.role)}</p>
                      <p style={{ margin: '8px 0 0', fontSize: 14, color: '#666' }}>{getRoleDescription(currentPlayer.role)}</p>
                    </>
                  ) : (
                    <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#666' }}>***</p>
                  )}
                </div>
                <Button variant="outline" onClick={() => setShowRole(!showRole)}>{showRole ? '숨기기' : '보기'}</Button>
              </div>
            </Card>
          )}
          {gameRoom.status === 'waiting' && (
            <div style={{ marginBottom: 24, padding: 12, background: '#e3f2fd', borderRadius: 8, color: '#1565c0' }}>
              교사가 역할을 배정할 때까지 기다려주세요.
            </div>
          )}
          <h3 style={{ marginBottom: 12, fontSize: 16 }}>참가자 ({gameRoom.players.length}명)</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
            {gameRoom.players.map((player) => (
              <Card key={player.id} padding="16px" style={{ border: '1px solid var(--Color-line-normal)' }}>
                <p style={{ margin: 0 }}>{player.name}{player.id === playerId && ' (나)'}</p>
                {player.role && gameRoom.status === 'role-assigned' && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{getRoleName(player.role)}</p>
                )}
              </Card>
            ))}
          </div>
          {gameRoom.status === 'role-assigned' && !currentPlayer?.role && (
            <div style={{ padding: 12, background: '#fff3e0', borderRadius: 8, color: '#e65100' }}>
              역할이 아직 배정되지 않았습니다. 잠시만 기다려주세요.
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default MafiaGameJoin;
